// src/components/GlobalDesignBar.tsx
import React from "react";
import { Box, Group, ActionIcon, Tooltip, Divider } from "@mantine/core";
import { FaRuler } from "react-icons/fa";
import { FiMaximize2, FiHelpCircle } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setMeasurementToolActiveInRedux,
  setMouseHelpModalOpenInRedux,
  setRequestZoomExtentInRedux,
} from "../designSlice";
import { RotationControl } from "./RotationControl";
import { BuildingStoryMenu } from "./BuildingStoryMenu";
import { RoomColoringMenu } from "../../RoomColoring/RoomColoringMenu";
import { DragSelectRoomsControl } from "./DragSelectRoomsControl";
import { MouseHelpModal } from "../../components/MouseHelpModal";
import { ViewerFreeTextSearch } from "./ViewerFreeTextSearch";
import { ComponentColoringMenu } from "./ComponentColoringMenu";
import { ShareViewButton } from "../SharedViews/ShareViewButton";

const ToolbarDivider = () => (
  <Divider orientation="vertical" h={24}
    style={{ alignSelf: "center", flexShrink: 0, minHeight: 24, maxHeight: 24 }} />
);

export const GlobalDesignBar: React.FC = () => {
  const dispatch = useAppDispatch();

  const dragSelectSpaces = useAppSelector((s) => s.design.dragSelectSpaces);
  const measurementToolActive = useAppSelector((s) => s.design.measurementToolActive);
  const mouseHelpModalOpen = useAppSelector((s) => s.design.mouseHelpModalOpen);

  return (
    <>
      <Box
        bg="white"
        px="md"
        py={6}
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          zIndex: 10,
        }}
      >
        {mouseHelpModalOpen && (
          <MouseHelpModal
            opened={mouseHelpModalOpen}
            onClose={() => dispatch(setMouseHelpModalOpenInRedux(false))}
          />
        )}
        <Group align="center" gap="sm">
          {/* Etasje, komponenter og rom */}
          <Group gap="sm" align="center">
            <BuildingStoryMenu />
            <ToolbarDivider />
            <Group gap="xs" wrap="nowrap">
              <ComponentColoringMenu />
              <ViewerFreeTextSearch mode="COMPONENT" width={210} />
            </Group>
            <ToolbarDivider />
            <Group gap="xs" wrap="nowrap">
              <RoomColoringMenu />
              <ViewerFreeTextSearch mode="ROOM" width={210} />
            </Group>
          </Group>

          <Group gap="sm" align="center" ml="auto">
            <ToolbarDivider />
            {/* Verktøy for visningen */}
            <DragSelectRoomsControl />

            <Tooltip label="Zoom extent" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                onClick={() => dispatch(setRequestZoomExtentInRedux())}
              >
                <FiMaximize2 size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Mål avstand" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                disabled={measurementToolActive || dragSelectSpaces}
                onClick={() => dispatch(setMeasurementToolActiveInRedux(true))}
              >
                <FaRuler size={16} />
              </ActionIcon>
            </Tooltip>

            <ToolbarDivider />

            {/* Blokk 4: rotasjon */}
            <RotationControl />

            <ToolbarDivider />

            {/* ✅ Hjelp (musekontroller) - lengst til høyre */}
            <Tooltip label="Musekontroller" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                aria-label="Musekontroller"
                onClick={() => dispatch(setMouseHelpModalOpenInRedux(true))}
              >
                <FiHelpCircle size={18} />
              </ActionIcon>
            </Tooltip>
            <ShareViewButton />
          </Group>
        </Group>
      </Box>
    </>
  );
};
